import MenuIcon from '@mui/icons-material/Menu';
import { Divider, IconButton, Menu } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import React, { useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

import { useLogout } from '@/api/userAccount';
import logo from '@/assets/sofar-logo-white.png';
import { LinkMenuItem, TopNavLink, TopNavTabs } from '@/components/TopNavBar';
import { AuthProvider } from '@/components/providers/AuthProvider';
import { useLoginRedirectUrl } from '@/hooks/useLoginRedirectUrl';
import { useGlobalStore } from '@/store';
import { blue } from '@/theme';

type NavLink = {
    label: string;
    path: string;
    asRegularLink?: boolean;
};

export const PublicTopNavBar = () => {
    const [searchParams] = useSearchParams();
    const loginUrl = useLoginRedirectUrl();
    const location = useLocation();
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);
    const setIsLoggedIn = useGlobalStore((state) => state.setIsLoggedIn);
    const { refetch: logout } = useLogout({ config: { enabled: false } });
    const mixpanel = useMixpanel();

    const backHref = useMemo(() => {
        if (location?.state && 'backToMapHref' in location.state) {
            return location.state.backToMapHref;
        }
        // pass user back to map so that if we came via a user's shared url, we maintain that context
        if (searchParams.has('user')) {
            return `/public?${new URLSearchParams({ user: searchParams.get('user') as string })}`;
        }
        return '/public';
    }, [location, searchParams]);

    const topLevelLinks: NavLink[] = useMemo(() => [{ label: 'Spotter Map', path: backHref }], [backHref]);

    // Nav menu
    const [navMenuAnchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseNavMenu = () => {
        setAnchorEl(null);
    };
    const handleLogout = async () => {
        await logout();
        setIsLoggedIn(false);
        mixpanel?.track('Logout');
        mixpanel?.reset();
        handleCloseNavMenu();
    };

    return (
        <AppBar
            enableColorOnDark
            position="static"
            elevation={0}
            sx={{
                backgroundColor: blue[700],
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Toolbar
                sx={{ flexWrap: 'wrap', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}
            >
                <Box sx={{ flexGrow: 0 }}>
                    <RouterLink to="/">
                        <Box sx={{ maxHeight: 35 }} component="img" src={logo} alt="sofar ocean logo" />
                    </RouterLink>
                </Box>

                <AuthProvider loadingChild={<></>} optional>
                    <TopNavTabs links={topLevelLinks} />

                    <Box sx={{ justifySelf: 'end' }}>
                        {isLoggedIn ? (
                            <>
                                {/* Nav menu */}
                                <IconButton
                                    size="large"
                                    aria-label="page navigation"
                                    aria-controls="navigation-menu"
                                    aria-haspopup="true"
                                    onClick={handleOpenNavMenu}
                                    color="inherit"
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Menu
                                    id="navigation-menu"
                                    anchorEl={navMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                borderRadius: 0,
                                            },
                                        },
                                    }}
                                    open={Boolean(navMenuAnchorEl)}
                                    onClose={handleCloseNavMenu}
                                >
                                    <Box
                                        sx={{
                                            /* break points must mirror topLevelLinks Box at top level above */
                                            display: { xs: 'initial', md: 'none' },
                                        }}
                                    >
                                        {topLevelLinks.map((page) => {
                                            return (
                                                <LinkMenuItem
                                                    key={page.label}
                                                    to={page.path}
                                                    asRegularLink={page.asRegularLink}
                                                    onClick={handleCloseNavMenu}
                                                >
                                                    {page.label}
                                                </LinkMenuItem>
                                            );
                                        })}
                                        <Divider />
                                    </Box>
                                    <LinkMenuItem to="/">Return to my Spotters</LinkMenuItem>
                                    <Divider />
                                    <LinkMenuItem onClick={handleLogout} to="#" color="error.main">
                                        Logout
                                    </LinkMenuItem>
                                </Menu>
                            </>
                        ) : (
                            <TopNavLink to={`${loginUrl}`}>Login</TopNavLink>
                        )}
                    </Box>
                </AuthProvider>
            </Toolbar>
        </AppBar>
    );
};
